import { useCallback, useEffect, useRef, useState } from "react";
import { ModalSize, useModalResize } from "./ModalWrapperContent";

interface ModalWrapperProps {
	children: React.ReactNode;
	size?: string;                              // string으로 유연하게 설정
	defaultWidth?: number;                      // 저장된 크기가 없을 때 적용할 기본 너비
	defaultHeight?: number;                     // 저장된 크기가 없을 때 적용할 기본 높이(px)
	sizeStorageKey?: string;                    // 리사이즈 결과를 저장할 localStorage 키
	overlay?: boolean;
	zIndex?: number;
	onOverlayClick?: () => void;
	onEscKeyDown?: () => void;
}

const ModalWrapper = ({ 
	children, 
	size = 'lg', 
	defaultWidth = 800,
	defaultHeight,
	sizeStorageKey,
	overlay = false, 
	zIndex = 10,
	onOverlayClick, 
	onEscKeyDown 
}: ModalWrapperProps) => {
	
	// size에 따른 height 자동 설정
	const getHeightBySize = (size: string) => {
		const heightMap: Record<string, number> = {
			hxs: 20,
			qxs: 25,
			xs: 30,
			hsm: 40,
			qsm: 45,
			sm: 50,
			qhmd: 55,
			hmd: 60,
			qmd: 65,
			md: 70,
			lg: 80,
			xl: 90,
			half: 50,    // 기존 값 추가
		};
		return heightMap[size] || 50;
	};

	const getStoredModalSize = (storageKey?: string): ModalSize | undefined => {
		if (!storageKey) return undefined;

		try {
			const storedValue = localStorage.getItem(storageKey);
			if (!storedValue) return undefined;

			const storedModalSize = JSON.parse(storedValue) as Partial<ModalSize>;
			if (
				typeof storedModalSize.width !== 'number' ||
				!Number.isFinite(storedModalSize.width) ||
				storedModalSize.width <= 0 ||
				typeof storedModalSize.height !== 'number' ||
				!Number.isFinite(storedModalSize.height) ||
				storedModalSize.height <= 0
			) {
				return undefined;
			}

			return {
				width: storedModalSize.width,
				height: storedModalSize.height,
			};
		} catch {
			return undefined;
		}
	};

	const [modalSize, setModalSize] = useState<ModalSize | undefined>(
		() => getStoredModalSize(sizeStorageKey),
	);
	const defaultHeightStyle = defaultHeight === undefined
		? `${getHeightBySize(size)}%`
		: `${defaultHeight}px`;
	const heightStyle = modalSize ? `${modalSize.height}px` : defaultHeightStyle;
	const modalRef = useRef<HTMLDivElement>(null);
	const handleResizeEnd = useCallback((resizedModalSize: ModalSize) => {
		setModalSize(resizedModalSize);

		if (!sizeStorageKey) return;

		try {
			localStorage.setItem(sizeStorageKey, JSON.stringify(resizedModalSize));
		} catch {
			// localStorage를 사용할 수 없는 환경에서도 현재 팝업의 리사이즈 결과는 유지한다.
		}
	}, [sizeStorageKey]);
	const { handleResizeMouseDown } = useModalResize(modalRef, handleResizeEnd);

	const handleEscKeyDown = useCallback((e: KeyboardEvent) => {
		// event.ctrlKey가 true이고 (Ctrl 키가 눌린 상태)
		// event.key가 'Escape'이면 (Escape 키가 눌렸을 때)
		if (e.key === 'Escape' && onEscKeyDown) {
			onEscKeyDown()
		}
	}, []); // useCallback으로 함수를 메모이제이션합니다.
	
	useEffect(() => {
		// 이벤트 리스너 추가
		window.addEventListener('keydown', handleEscKeyDown);
		// cleanup 함수에서 리스너 제거
		return () => {
			window.removeEventListener('keydown', handleEscKeyDown);
		};
	}, [])


	return (
		<div className="fixed inset-0 flex justify-center items-center" style={{ zIndex }}>
			{overlay && <div className="w-full h-full absolute bg-black opacity-[0]" onClick={onOverlayClick} />}
			<div 
				ref={modalRef}
				className="relative flex flex-col bg-white shadow-xl rounded-[8px] z-10 border-2 border-black"
				style={{ 
					width: `${modalSize?.width ?? defaultWidth}px`,
					height: heightStyle,
					maxHeight: modalSize ? 'none' : heightStyle
				}}
			>
				<div className="flex-1 w-full h-full overflow-auto">
					{children}
				</div>
				<div
					className="absolute bottom-1 right-0.5 w-4 h-4 cursor-nwse-resize z-50 flex items-end justify-end p-0.5"
					onMouseDown={handleResizeMouseDown}
				>
					<div className="w-full h-full border-r-2 border-b-2 border-gray-400"></div>
				</div>
			</div>
		</div>
	);
};

export default ModalWrapper;
